import { GameManual } from '../types';

const h = (text: string) => ({ type: 'heading' as const, text, level: 2 as const });
const p = (text: string) => ({ type: 'paragraph' as const, text });
const list = (steps: string[]) => ({ type: 'step-list' as const, steps });
const warning = (text: string) => ({ type: 'warning' as const, text });
const tip = (text: string) => ({ type: 'tip' as const, text });
const diagram = (
  diagramId: 'travel' | 'complex' | 'lss-timing' | 'scoring-example',
  alt: string,
  fallbackText: string
) => ({ type: 'diagram' as const, diagramId, alt, fallbackText });

export const onMarsManual: GameManual = {
  chapters: [
    {
      title: 'Cel gry',
      content: [
        h('Cel rozgrywki'),
        warning(
          'Ta cyfrowa instrukcja obsługuje wyłącznie 3–4 graczy (supportedPlayerCounts: 3, 4). Tryb solo i zasady dla 1–2 graczy nie są dostępne w aplikacji — nie wybierzesz ich w konfiguracji.'
        ),

        p('Gracze kierują prywatnymi kompaniami uczestniczącymi w budowie pierwszej samowystarczalnej kolonii na Marsie. Zwycięża gracz z największą liczbą Punktów Szansy (PS).'),
        p('PS zdobywa się podczas gry i na jej końcu m.in. za budowę i ulepszanie infrastruktury, rozwój technologii, postęp kolonii, statki, naukowców, kontrakty, plany oraz realizację celów prywatnych.'),
        h('Struktura gry'),
        p('Gra trwa kilka rund. Każda runda składa się z Fazy Kolonizacji, w której każdy gracz wykonuje dokładnie jedną turę, oraz Fazy Wahadłowca, w której wahadłowiec przesuwa się bliżej podróży albo przelatuje pomiędzy orbitą i kolonią.'),
        h('Uruchomienie końca gry'),
        p('Koniec gry uruchamia się po ukończeniu wymaganej liczby misji, modyfikowanej przez poziom kolonii. Dokończcie bieżącą rundę, następnie rozegrajcie jeszcze jedną pełną Fazę Kolonizacji. W tej ostatniej rundzie pomija się Fazę Wahadłowca.'),
      ],
    },
    {
      title: 'Najważniejsze pojęcia',
      content: [
        h('Orbita i kolonia'),
        p('Lewa część planszy to stacja orbitalna, a pozostała część to kolonia na powierzchni. Położenie znacznika gracza określa, z której grupy akcji głównych może korzystać.'),
        h('Zasób ogólny i magazyn orbitalny'),
        p('Element otrzymywany bez dodatkowego wskazania pobiera się z zasobu ogólnego. Z magazynu orbitalnego pobiera się go tylko wtedy, gdy efekt wyraźnie pokazuje ikonę magazynu.'),
        h('Magazyn gracza'),
        p('Zasoby przechowuje się na planszy gracza. Limit każdego rodzaju zasobu wynosi liczba własnych schronień na Marsie plus 1. Nadwyżka wraca do zasobu ogólnego.'),
        h('Kwatery mieszkalne'),
        p('Kolonistów otrzymanych podczas gry umieszcza się w kwaterach, jeśli jest miejsce. Pojemność wynosi 4 plus 2 za każde dodatkowe wybudowane schronienie. Nadmiar trafia do osobistego zapasu obok planszy gracza.'),
        h('Kryształy'),
        p('Nowo zdobyte kryształy kładzie się pod depozytem. Nie można ich wydać w tej samej turze. Na początku kolejnej tury przenosi się je do wolnych miejsc depozytu; nadmiar wraca do zasobu ogólnego.'),
        h('Minerały'),
        p('Minerał może zastąpić dowolny podstawowy zasób, lecz nie kryształ. Nie może zastępować zasobu wymaganego do realizacji kontraktu ziemskiego.'),
        h('Kompleks'),
        p('Kompleks to co najmniej dwa połączone budynki tego samego typu. Jego wielkość to liczba wszystkich połączonych kafli tego typu. Pojedynczy budynek nie jest kompleksem.'),
        h('Pusty heks'),
        p('Pusty heks nie zawiera żadnego elementu. Na końcu tury na jednym heksie może znajdować się najwyżej jeden z elementów: bot, łazik, kolonista albo znacznik budynku zaawansowanego.'),
        h('Kolejność graczy'),
        p('Kolejność wynika z pól kolejności od 1 do 8. Rozpatruje się ją od najniższego zajętego numeru do najwyższego.'),
      ],
    },
    {
      title: 'Elementy gry',
      content: [
        h('Plansze i pomoce'),
        list([
          '1 plansza główna, 4 plansze graczy, plansza naukowców i kontraktów oraz ekspozytor kafli budynków.',
          '4 pomoce graczy, instrukcja i księga odniesienia.',
        ]),
        h('Budynki i technologie'),
        list([
          '5 początkowych kafli budynków, 30 zwykłych kafli budynków oraz 20 kafli schronień.',
          '16 kafli technologii i 4 technologie schronień.',
          '24 karty planów i 32 znaczniki budynków zaawansowanych.',
        ]),
        h('Pionki graczy'),
        list([
          'Po 12 kolonistów, 4 boty, 1 łazik, 5 statków, 5 kostek postępu, znacznik gracza i znacznik PS w każdym kolorze.',
          '6 pionków naukowców.',
        ]),
        h('Karty, kafle i zasoby wspólne'),
        list([
          'Po 20 minerałów, baterii, wody, roślin i tlenu oraz 30 kryształów.',
          '8 kafli badań, 19 kafli odkryć i 8 kafli nagród LSS.',
          'Karty naukowców, kontraktów, celów prywatnych, misji oraz zestawy wariantów.',
        ]),
        tip('Tylko kryształy są nielimitowane. Jeżeli zabraknie ich znaczników, użyj zamienników. Zasoby i pozostałe komponenty są limitowane.'),
      ],
    },
    {
      title: 'Przygotowanie dla 3 graczy',
      content: [
        h('Plansza główna'),
        list([
          'Połóż planszę główną na środku. Lewa strona to orbita, prawa i środkowa część to powierzchnia.',
          'Potasuj kafle odkryć, utwórz zakryty stos i odkryj 2 kafle na polu eksploracji.',
          'W magazynie orbitalnym połóż po 3 kryształy, baterie, wodę, rośliny i tlen.',
          'Odłóż technologie schronień. Pozostałe technologie podziel według rewersów. Jeden zestaw potasuj i rozłóż odkryty na siatce technologii; drugi utwórz w dwóch zakrytych stosach.',
          'Oddziel plany poziomu 1 i 3, potasuj osobno, połóż poziom 1 na poziomie 3 i odkryj 6 kart.',
          'Na planszy naukowców rozłóż karty w kolejności: geolog, inżynier badań i rozwoju, hydrolog, biochemik, geochemik, inżynier systemów. Na każdej ustaw odpowiedni pionek.',
          'Połóż zakrytą talię kontraktów bez tasowania.',
          'Umieść początkową kopalnię na centralnym heksie, a pozostałe budynki startowe losowo na wskazanych polach, zachowując wspólną orientację.',
          'Podziel zwykłe kafle budynków według typów, potasuj i połóż na ekspozytorze.',
          'Kafle badań podziel według typu i połóż na wskazanych polach mapy.',
        ]),
        h('Misje i LSS'),
        list([
          'Do gry krótkiej wybierz 2 krótkie i 1 długą misję; do długiej 1 krótką i 2 długie.',
          'Przy każdej misji ustaw jej znacznik przy powiązanej akcji i kostkę śledzenia na polu dla 3 graczy.',
          'Znacznik pozostałych misji ustaw na 3.',
          'Znacznik poziomu kolonii ustaw przed drugim rzędem toru LSS, a cztery znaczniki LSS na początkowych polach dolnego rzędu.',
          'Wylosuj 4 nagrody LSS i połóż je na górnych polach.',
        ]),
        h('Każdy gracz'),
        list([
          'Bierze planszę, znacznik gracza i znacznik PS; znacznik PS ustawia na 0.',
          'Kładzie po 1 zasobie każdego rodzaju w magazynie oraz 1 kryształ w depozycie.',
          'Umieszcza technologię schronienia na środkowym polu drugiej kolumny laboratorium.',
          'Kładzie 5 statków na górnych polach depozytu.',
          'Cztery kafle schronień kładzie przy planszy, a piąty jako początkowe schronienie na najbliższym oznaczonym heksie startowym.',
          'Jednego bota ustawia na schronieniu, trzy obok planszy. Łazik i znaczniki budynków zaawansowanych pozostają obok planszy.',
          'Trzech kolonistów umieszcza w kwaterach, dziewięciu obok planszy.',
          'Pięć kostek postępu umieszcza pod pięcioma zasobami.',
          'Otrzymuje 3 tajne cele prywatne.',
        ]),
        h('Kolejność i wahadłowiec'),
        list([
          'Wybierz pierwszego gracza.',
          'Od pierwszego gracza zgodnie z ruchem wskazówek zegara każdy wybiera wolne pole kolejności 1–8 i może natychmiast otrzymać jego premię.',
          'Gracz na najwyższym numerze ustawia wahadłowiec na czerwonym polu 1 po wybranej stronie planszy.',
        ]),
      ],
    },
    {
      title: 'Przygotowanie dla 4 graczy',
      content: [
        h('Zasady wspólne'),
        p('Wykonaj przygotowanie jak dla 3 graczy, z poniższymi zmianami. Magazyn nadal zawiera po 3 elementy każdego typu, używa się obu zestawów technologii, sześciu odkrytych planów i trzech misji.'),
        h('Zmiany'),
        list([
          'Kostki misji ustaw na polach wymagań dla 4 graczy.',
          'Każdy gracz umieszcza początkowe schronienie na jednym z czterech oznaczonych heksów startowych, najlepiej najbliższym swojemu miejscu.',
          'Na każdym schronieniu ustaw odpowiedniego bota.',
          'Pozostałe przygotowanie osobiste jest identyczne.',
        ]),
      ],
    },
    {
      title: 'Układ planszy',
      content: [
        h('Orbita'),
        list([
          'Siatka technologii i dwa stosy technologii.',
          'Sześć odkrytych kart planów.',
          'Magazyn orbitalny.',
          'Pola akcji orbitalnych oraz pola kolejności 1–4.',
          'Pola podróży wahadłowca.',
        ]),
        h('Powierzchnia'),
        list([
          'Heksagonalna mapa kolonii.',
          'Początkowe budynki i schronienia.',
          'Pola akcji kolonii oraz pola kolejności 5–8.',
          'Obszar postępu i tory LSS.',
          'Pole eksploracji z kaflami odkryć oraz kafle badań na mapie.',
        ]),
        h('Obok planszy'),
        list([
          'Plansza naukowców i kontraktów.',
          'Ekspozytor kafli budynków.',
          'Zasób ogólny, talia planów i stos odkryć.',
        ]),
      ],
    },
    {
      title: 'Obieg zasobów',
      content: [
        h('Pięć podstawowych zasobów'),
        p('Baterie, woda, rośliny, tlen i minerały służą do wykonywania akcji, budowy, ulepszeń, kontraktów i aktywowania premii.'),
        h('Zdobywanie'),
        list([
          'Zasób z zasobu ogólnego trafia do magazynu gracza.',
          'Zasób z ikoną magazynu pobiera się z odpowiedniego pola magazynu orbitalnego.',
          'Budowa budynku daje zasób odpowiadający typowi budynku: 1 za samotny budynek albo tyle, ile wynosi nowa wielkość kompleksu.',
          'Produkcja podczas podróży na orbitę zapewnia zasoby z własnych budynków zaawansowanych oraz minerały z kopalń.',
        ]),
        h('Wydawanie'),
        p('Koszt oddaje się do zasobu ogólnego. Minerał może zastąpić baterię, wodę, roślinę lub tlen, z wyjątkiem dostaw na kontrakty.'),
        warning('Nie pobieraj elementów z magazynu orbitalnego, jeśli efekt nie pokazuje ikony magazynu.'),
      ],
    },
    {
      title: 'Kryształy i magazyn',
      content: [
        h('Zdobywanie kryształów'),
        p('Kryształy otrzymuje się m.in. za udział w misjach, budowę schronień, wybrane pola kolejności, kafle odkryć i niektóre efekty.'),
        h('Opóźniona dostępność'),
        p('Kryształy zdobyte w turze trafiają pod depozyt. Na początku następnej własnej tury przenosi się je do wolnych miejsc depozytu.'),
        h('Wydawanie'),
        p('Kryształy służą do wzmacniania działań, opłacania zatłoczonych akcji, wykonywania akcji wykonawczych i wybranych efektów.'),
        h('Miejsce w depozycie'),
        p('Na początku gry część depozytu zajmują statki. Po przyjmowaniu statków zwalniają się miejsca na kryształy. Nadmiar kryształów przy przenoszeniu do depozytu przepada.'),
      ],
    },
    {
      title: 'Koloniści',
      content: [
        h('Trzy miejsca kolonistów'),
        list([
          'Kwatery mieszkalne: dostępni do kosztów i wzmacniania akcji.',
          'Strefa pracy: kolonista użyty do wzmacniania akcji lub opłacenia zatłoczonego pola.',
          'Pola akcji: kolonista wymagany przez akcje oznaczone czerwonym symbolem.',
        ]),
        h('Czerwony kolonista — zajęcie pola akcji'),
        p('Aby wykonać akcję oznaczoną czerwonym kolonistą, umieść kolonistę z kwater na wolnym polu tej akcji. Jeżeli na akcji znajdują się koloniści innych graczy, zapłać po 1 krysztale albo przenieś 1 kolonistę do strefy pracy za każdy obecny inny kolor. Można łączyć oba sposoby zapłaty.'),
        p('Jeżeli wszystkie pola akcji są pełne, najpierw usuń kolonistów gracza lub graczy mających tam najwięcej pionków i przenieś ich do odpowiednich stref pracy. Następnie oblicz ewentualny koszt na podstawie pozostałych kolorów.'),
        h('Turkusowy kolonista — wzmocnienie'),
        p('Przy akcji oznaczonej turkusowym kolonistą możesz przenosić kolonistów z kwater do strefy pracy, aby zwiększać liczbę otrzymywanych elementów albo chwilowo podnosić poziom technologii.'),
        h('Powrót'),
        p('Podczas podróży odzyskujesz kolonistów z pól akcji po stronie, którą właśnie opuszczasz, oraz ze swojej strefy pracy. Nadmiar ponad pojemność kwater trafia do osobistego zapasu.'),
      ],
    },
    {
      title: 'Boty',
      content: [
        h('Strefa budowy'),
        p('Bot wyznacza strefę budowy obejmującą jego heks i wszystkie sześć sąsiednich heksów. Nowy budynek lub ulepszenie musi zostać wykonane w strefie co najmniej jednego własnego bota.'),
        h('Ruch'),
        p('W Centrum Kontroli możesz poruszać botami i łazikiem. Jeden punkt ruchu przesuwa element o jeden sąsiedni heks. Na koniec tury heks może zawierać tylko jeden pionek lub znacznik budynku zaawansowanego.'),
        h('Przemieszczenie'),
        p('Jeżeli budowa lub ulepszenie przykrywa heks z botem albo łazikiem, właściciel przesuwa go na najbliższy legalny heks.'),
        tip('Przed planowaną budową sprawdź nie tylko miejsce kafla, lecz także pozycję bota, który ma obejmować ten heks swoją strefą.'),
      ],
    },
    {
      title: 'Łaziki',
      content: [
        h('Wprowadzenie na mapę'),
        p('Łazik początkowo znajduje się obok planszy. Wprowadza się go dzięki odpowiednim efektom i porusza przez Centrum Kontroli lub akcje wykonawcze.'),
        h('Ruch i kryształy'),
        p('Łazik zbiera kryształ, gdy przechodzi przez heks z kryształem. Kryształ trafia pod depozyt.'),
        h('Kafle odkryć i badań'),
        p('Aby zdobyć kafel odkrycia albo badań, łazik musi zakończyć ruch na jego heksie. Musisz być w stanie otrzymać co najmniej część korzyści. Rozpatrz koszt i efekt natychmiast, a zdobyty kafel połóż przy planszy.'),
        warning('Łazik nie może zakończyć ruchu na heksie, jeżeli nie może rozpatrzyć żadnej części jego korzyści.'),
      ],
    },
    {
      title: 'Statki',
      content: [
        h('Statki prywatne'),
        p('Każdy gracz zaczyna z pięcioma statkami w depozycie. Przyjęty statek trafia do hangaru, zwalniając miejsce na kryształy i sprowadzając nowych kolonistów.'),
        h('Podróż poza kursem wahadłowca'),
        p('Jeżeli chcesz podróżować, ale nie korzystasz z darmowego przelotu wahadłowcem, musisz odrzucić jeden statek z hangaru do pudełka.'),
        h('Punktacja'),
        p('Na końcu gry każdy statek pozostający w hangarze daje 3 PS.'),
      ],
    },
    {
      title: 'Technologia',
      content: [
        h('Poziom technologii'),
        p('Poziom technologii wynika z kolumny laboratorium, w której leży kafel. Im dalej w prawo, tym wyższy poziom i większa wartość końcowa.'),
        h('Zastosowanie'),
        p('Technologie umożliwiają tworzenie i powiększanie kompleksów, zwiększają ruch, liczbę pobieranych elementów lub siłę konkretnych działań.'),
        h('Cudze technologie'),
        p('Możesz użyć technologii innego gracza. Po użyciu właściciel przesuwa ją o jedno pole w prawo, jeśli to możliwe, i otrzymuje premię nowego pola.'),
        h('Chwilowe podnoszenie poziomu'),
        p('Jeżeli działanie pokazuje turkusowego kolonistę, możesz przenieść kolonistów do strefy pracy, zwiększając poziom technologii o 1 za każdego.'),
        h('Punktacja'),
        p('Na końcu gry technologia daje PS zgodnie z kolumną laboratorium, w której się znajduje.'),
      ],
    },
    {
      title: 'Budynki i kompleksy',
      content: [
        h('Typy budynków'),
        list([
          'Kopalnia — zapewnia minerały; przy budowie wymaga kolonisty zamiast zasobu.',
          'Generator — produkuje baterie i rozwija tor energii.',
          'Ekstraktor wody — produkuje wodę i rozwija tor wody.',
          'Szklarnia — produkuje rośliny i rozwija tor żywności.',
          'Kondensator tlenu — produkuje tlen i rozwija tor tlenu.',
          'Schronienie — zwiększa pojemność magazynu i kwater; przy budowie daje kryształy.',
        ]),
        h('Samotny budynek'),
        p('Można go zbudować dokładnie dwa heksy od istniejącego budynku tego samego typu i nie może sąsiadować z innym budynkiem tego typu. Nie wymaga technologii.'),
        h('Kompleks'),
        p('Budowa sąsiadująca z budynkiem tego samego typu tworzy lub powiększa kompleks i wymaga odpowiedniej technologii. Wymagany poziom jest co najmniej równy liczbie budynków, z którymi nowy kafel zostanie połączony bezpośrednio i pośrednio.'),
        h('Diagram — legalny i nielegalny kompleks'),
        diagram(
          'complex',
          'Diagram legalnego i nielegalnego kompleksu budynków',
          'Legalny: generator B sąsiaduje z A przy technologii ≥1. Legalny powiększony: C łączy kompleks wielkości 2 przy technologii ≥2. Nielegalny: sąsiedztwo bez technologii lub samotny z jednoczesnym sąsiedztwem.'
        ),
        tip(
          'Legalny: masz generator A. Kładziesz drugi generator B na heksie sąsiadującym z A, używając technologii generatorów poziomu ≥ 1 (łączysz 1 budynek). Powstaje kompleks wielkości 2.'
        ),
        tip(
          'Legalny powiększony: kompleks generatorów A–B wielkości 2. Kładziesz C sąsiadująco tak, że łączy się z A i B (bezpośrednio lub przez łańcuch). Potrzebujesz technologii poziomu ≥ 2, bo łączysz dwa istniejące budynki w jeden kompleks wielkości 3.'
        ),
        warning(
          'Nielegalny: kładziesz generator obok generatora bez technologii generatorów albo z poziomem zbyt niskim względem liczby budynków, które nowy kafel połączy. Takiej budowy nie wolno wykonać.'
        ),
        warning(
          'Nielegalny „samotny”: kafel dokładnie 2 heksy od innego generatora, ale jednocześnie sąsiaduje z trzecim generatorem — to już nie samotny budynek; traktuj go jako próbę kompleksu i wymagaj technologii.'
        ),
        h('Kontrprzykłady'),
        list([
          'Samotny budynek nie przesuwa kostki postępu — nawet jeśli później stanie się częścią kompleksu, kostkę przesuwasz dopiero przy pierwszym utworzeniu lub powiększeniu kompleksu tego typu.',
          'Schronienie sąsiadujące z innym schronieniem tworzy kompleks schronień i wymaga technologii schronień, ale nie przenosi kostki postępu.',
          'Budowa generatora obok ekstraktora wody nie tworzy kompleksu — typy muszą być identyczne.',
        ]),
        h('Postęp'),
        p('Pierwsze utworzenie lub powiększenie kompleksu danego typu pozwala przenieść odpowiadającą kostkę postępu do obszaru postępu. Schronienia nie dają tego ruchu.'),
      ],
    },
    {
      title: 'Poziom kolonii LSS',
      content: [
        h('Cztery tory LSS'),
        p('Energia, woda, rośliny i tlen mają osobne znaczniki. Budowa odpowiadającego budynku przesuwa znacznik, jeśli jest to możliwe.'),
        h('Kolejność po budowie budynku LSS'),
        diagram(
          'lss-timing',
          'Diagram kolejności LSS, nagród, wzrostu kolonii i misji',
          'Budowa LSS → ruch znacznika → nagroda → sprawdź wzrost (4 tory powyżej poziomu) → aktualizacja poziomu → potem misje z tej budowy.'
        ),
        list([
          'Zbuduj generator, ekstraktor wody, szklarnię albo kondensator tlenu zgodnie z zasadami budowy.',
          'Natychmiast przesuń odpowiadający znacznik LSS o jedno pole (jeśli tor na to pozwala).',
          'Jeśli znacznik wszedł na pole z nagrodą — rozpatrz nagrodę LSS teraz, w kolejności graczy gdy kilku graczy rozpatruje korzyści.',
          'Sprawdź warunek wzrostu: czy wszystkie cztery znaczniki LSS są już powyżej znacznika poziomu kolonii?',
          'Jeśli tak — wykonaj aktualizację poziomu kolonii (poniżej) zanim przejdziesz do kolejnych efektów tury.',
          'Dopiero potem sprawdź wkłady misji wynikające z tej samej budowy, jeśli misja na to wskazuje.',
        ]),
        h('Rząd potrzeb kolonii'),
        p('Znacznik poziomu kolonii wskazuje aktualny rząd. Gdy wszystkie cztery znaczniki LSS znajdą się powyżej znacznika poziomu kolonii, poziom kolonii wzrasta.'),
        h('Nagrody LSS'),
        p('Przesunięcie znacznika LSS może uruchomić nagrodę wskazaną na torze. Jeżeli kilku graczy rozpatruje korzyści, stosuje się kolejność graczy.'),
        h('Wzrost kolonii'),
        list([
          'Przesuń znacznik poziomu kolonii do kolejnego rzędu.',
          'Rozpatrz punktację kostek postępu, jeśli nowy poziom ją uruchamia.',
          'Uzupełnij technologie i plany zgodnie z oznaczeniami poziomu.',
          'Zmniejsz liczbę misji potrzebnych do końca gry zgodnie z nowym poziomem.',
        ]),
        h('Kontrprzykłady timingowe'),
        list([
          'Nie przesuwaj LSS „na koniec rundy” — ruch jest natychmiastowy po legalnej budowie budynku LSS.',
          'Nie pomijaj sprawdzenia wzrostu, gdy tylko trzy z czterech torów są powyżej poziomu — wzrost wymaga wszystkich czterech.',
          'Kopalnia i schronienie nie przesuwają torów LSS, nawet jeśli budujesz je w tej samej turze co generator.',
          'Ukończenie misji i wzrost LSS to osobne triggery; oba mogą w tej samej rundzie zbliżyć znacznik pozostałych misji do końca gry.',
        ]),
        warning('Poziom kolonii może sam uruchomić koniec gry nawet bez ukończenia wszystkich trzech misji.'),
      ],
    },
    {
      title: 'Misje',
      content: [
        h('Wspólne cele'),
        p('Każda z trzech misji opisuje działanie lub osiągnięcie kolonii. Gdy gracz wnosi wkład, otrzymuje wskazaną liczbę kryształów i przesuwa kostkę misji o jedno pole.'),
        h('Ukończenie'),
        p('Po osiągnięciu pola z haczykiem usuń kartę misji i przesuń znacznik pozostałych misji o jedno pole.'),
        h('Wpływ poziomu kolonii'),
        list([
          'Na poziomie 3 do uruchomienia końca gry wystarczą 2 ukończone misje.',
          'Na poziomie 4 wystarczy 1 ukończona misja.',
          'Na poziomie 5 koniec gry uruchamia się niezależnie od misji.',
        ]),
      ],
    },
    {
      title: 'Cele prywatne',
      content: [
        h('Realizacja celu'),
        p('Każdy zaczyna z trzema tajnymi kartami. Gdy w swojej turze spełniasz wymagania jednej karty, możesz ją ujawnić i wybrać jedną z przedstawionych nagród. W całej grze możesz zrealizować tylko jeden cel.'),
        h('Użycie jako kryształu'),
        p('W swojej turze możesz odrzucić dowolny niezrealizowany cel i użyć karty tak, jakby była jednym kryształem. Nie otrzymujesz fizycznego kryształu.'),
      ],
    },
    {
      title: 'Kontrakty',
      content: [
        h('Pozyskanie'),
        p('Kontrakt ziemski bierze się w ramach akcji zatrudnienia naukowca lub pozyskania kontraktu. Karty leżą w ustalonej talii i nie są tasowane.'),
        h('Dostawy'),
        p('Elementy wymagane przez kontrakt można w trakcie gry umieszczać na karcie. Minerały nie zastępują innych zasobów dostarczanych na kontrakt.'),
        h('Koniec gry'),
        p('Ukończony kontrakt daje wskazane PS. Nieukończony może powodować utratę PS. Podczas końcowej punktacji możesz jeszcze przenieść posiadane zasoby i kryształy na kontrakty.'),
      ],
    },
    {
      title: 'Kolejność tury',
      content: [
        h('Początek własnej tury'),
        list([
          'Przenieś kryształy spod depozytu do wolnych miejsc depozytu.',
          'Sprawdź swoje aktualne położenie: orbita albo kolonia.',
        ]),
        h('Wykonanie tury'),
        list([
          'Wykonaj dokładnie 1 akcję główną dostępną po swojej stronie planszy.',
          'Możesz wykonać najwyżej 1 akcję wykonawczą — przed albo po akcji głównej.',
          'W dowolnym momencie swojej tury możesz zrealizować jeden cel prywatny, jeżeli spełniasz wymagania.',
          'Sprawdź postęp misji wynikający z wykonanych działań.',
          'Po zakończeniu połóż swój znacznik gracza, aby oznaczyć wykonaną turę.',
        ]),
        warning('Akcja wykonawcza nie zastępuje obowiązkowej akcji głównej.'),
      ],
    },
    {
      title: 'Pobyt na orbicie',
      content: [
        h('Dostępne akcje główne'),
        list([
          'Kapsuła desantowa.',
          'Pozyskanie planu.',
          'Nauka nowej technologii.',
          'Badania i rozwój.',
          'Uzupełnienie zaopatrzenia.',
        ]),
        p('Nie możesz wykonywać głównych akcji kolonii, dopóki twój znacznik znajduje się po stronie orbity. Akcje wykonawcze mogą zależeć od planów i statków, a nie od strony planszy.'),
      ],
    },
    {
      title: 'Podróż na powierzchnię',
      content: [
        h('Diagram — orbita → powierzchnia'),
        diagram(
          'travel',
          'Diagram podróży między orbitą a powierzchnią',
          'Orbita → opuszczasz orbitę → odzyskujesz kolonistów z pól akcji orbity i strefy pracy → pole kolejności kolonii. Powierzchnia → produkcja → koloniści z kolonii → pole kolejności orbity. Zawsze zbierasz stronę opuszczaną.'
        ),
        tip(
          'Orbita (akcje orbitalne) → opuszczasz orbitę → odzyskujesz kolonistów z pól akcji ORBITY i ze strefy pracy → zajmujesz pole kolejności KOLONII. Produkcja budynków zaawansowanych NIE następuje przy tej podróży.'
        ),
        h('Krok 1 — eksploracja'),
        p('Wybierz jeden odkryty kafel odkrycia i połóż go na pustym heksie dokładnie 3 pola od swojego łazika. Jeżeli łazik nie jest jeszcze na mapie, licz od początkowej kopalni. Jeżeli nie ma legalnego miejsca, pomiń ułożenie. Następnie uzupełnij ekspozycję odkryć.'),
        h('Krok 2 — odzyskanie kolonistów'),
        p('Podczas podróży na powierzchnię odzyskaj kolonistów z pól akcji po stronie orbity oraz ze swojej strefy pracy. Umieść ich w kwaterach; nadmiar ponad pojemność trafia do osobistego zapasu.'),
        warning(
          'Kontrprzykład: lecąc na powierzchnię NIE zbierasz kolonistów z pól akcji kolonii — tam ich jeszcze nie używałeś w tej fazie; zbierasz stronę, którą opuszczasz (orbitę).'
        ),
        h('Krok 3 — nowe pole kolejności'),
        p('Ustaw swój znacznik na dowolnym pustym polu kolejności po stronie kolonii i możesz otrzymać jego premię, opłacając ewentualny koszt.'),
      ],
    },
    {
      title: 'Pobyt na powierzchni',
      content: [
        h('Dostępne akcje główne'),
        list([
          'Centrum Kontroli.',
          'Budowa budynku.',
          'Ulepszenie budynku.',
          'Przyjęcie statku.',
          'Zatrudnienie naukowca albo pozyskanie kontraktu.',
        ]),
        p('Na powierzchni zarządzasz rozmieszczeniem botów i łazika, rozbudowujesz kolonię, tworzysz budynki zaawansowane, sprowadzasz kolonistów i pozyskujesz specjalistów.'),
      ],
    },
    {
      title: 'Podróż na orbitę',
      content: [
        h('Diagram — powierzchnia → orbita'),
        tip(
          'Kolonia (akcje powierzchni) → produkcja z budynków zaawansowanych i kopalń → odzyskujesz kolonistów z pól akcji KOLONII i ze strefy pracy → zajmujesz pole kolejności ORBITY.'
        ),
        h('Krok 1 — produkcja'),
        list([
          'Każdy własny budynek zaawansowany produkuje 1 zasób odpowiadający typowi budynku.',
          'Budynek zaawansowany na schronieniu produkuje 1 kryształ.',
          'Każdy własny kolonista lub budynek zaawansowany w kopalni produkuje 1 minerał.',
        ]),
        h('Krok 2 — odzyskanie kolonistów'),
        p('Koloniści z kolonijnych pól akcji oraz ze strefy pracy wracają do kwater; nadmiar trafia do osobistego zapasu.'),
        warning(
          'Kontrprzykład: lecąc na orbitę NIE zbierasz kolonistów z pól akcji orbity — zbierasz stronę, którą opuszczasz (kolonię). Zwykłe budynki bez znacznika zaawansowanego nie produkują.'
        ),
        h('Krok 3 — nowe pole kolejności'),
        p('Ustaw znacznik na pustym polu kolejności po stronie orbity i możesz otrzymać jego premię.'),
      ],
    },
    {
      title: 'Akcje główne na orbicie',
      content: [
        h('Kapsuła desantowa'),
        p('Natychmiast podróżujesz na powierzchnię według zasad podróży, lecz bez umieszczania kafla odkrycia. Po zajęciu pola kolejności po stronie kolonii połóż znacznik, ponieważ twoja tura w tej rundzie już się zakończyła.'),
        h('Pozostałe akcje'),
        p('Pozyskanie planu, nauka technologii, badania i rozwój oraz uzupełnienie zaopatrzenia zostały opisane w osobnych rozdziałach.'),
      ],
    },
    {
      title: 'Pozyskanie planu',
      content: [
        h('Przebieg'),
        list([
          'Jeżeli akcja wymaga kolonisty, zajmij pole i opłać zatłoczenie.',
          'Wybierz jedną odkrytą kartę planu.',
          'Otrzymaj zasób albo kryształ wskazany w prawym dolnym rogu.',
          'Połóż plan przy planszy i umieść na nim niewykorzystany znacznik budynku zaawansowanego.',
        ]),
        p('Dopóki znacznik leży na planie, nie możesz używać jego akcji wykonawczej. Znacznik zdejmiesz, gdy ulepszysz budynek tym planem.'),
        h('Wzmocnienie'),
        p('Za każdego kolonistę wysłanego do strefy pracy możesz pozyskać dodatkowy plan, stosując te same zasady. Nie uzupełniaj ekspozycji od razu.'),
      ],
    },
    {
      title: 'Nauka nowej technologii',
      content: [
        h('Wybór kafla'),
        p('Weź technologię z siatki i połóż ją na pustym polu lewej kolumny laboratorium. Jeżeli oba pola są zajęte, nie możesz wykonać tej akcji.'),
        h('Koszt'),
        list([
          'Dolny rząd siatki: bez kosztu.',
          'Środkowy rząd: 1 bateria.',
          'Górny rząd: 1 bateria i 1 dowolny zasób.',
        ]),
        h('Premia laboratorium'),
        p('Po umieszczeniu kafla otrzymujesz premię wydrukowaną na polu. Jeżeli zawiera turkusowego kolonistę, możesz ją wzmacniać.'),
        h('Wzmocnienie'),
        p('Za kolonistę wysłanego do strefy pracy możesz wziąć dodatkową technologię, jeśli masz dla niej wolne miejsce. Siatka uzupełnia się dopiero podczas wzrostu kolonii.'),
      ],
    },
    {
      title: 'Uzupełnianie magazynu',
      content: [
        h('Pobranie zaopatrzenia'),
        p('Wybierz jeden rodzaj elementu dostępnego w magazynie orbitalnym i pobierz 1 sztukę. Zasób trafia do magazynu gracza, a kryształ pod depozyt.'),
        h('Wzmocnienie'),
        p('Za każdego kolonistę przeniesionego do strefy pracy pobierz dodatkowy element tego samego albo innego dostępnego rodzaju, zgodnie z ikonografią akcji.'),
        warning('Magazyn orbitalny jest ograniczony. Nie można pobrać elementu, którego nie ma na odpowiednim polu.'),
      ],
    },
    {
      title: 'Akcje główne na powierzchni',
      content: [
        h('Dostępne działania'),
        list([
          'Centrum Kontroli: ruch botów i łazika.',
          'Budowa: postawienie nowego budynku.',
          'Ulepszenie: przekształcenie budynku w budynek zaawansowany.',
          'Przyjęcie statku: sprowadzenie kolonistów i statku do hangaru.',
          'Naukowiec lub kontrakt: pozyskanie specjalisty albo karty kontraktu.',
        ]),
      ],
    },
    {
      title: 'Budowa budynku',
      content: [
        h('Wybór budynku'),
        p('Wybierz kopalnię, generator, ekstraktor wody, szklarnię, kondensator tlenu albo najniżej położone własne schronienie.'),
        h('Koszt'),
        p('Zapłać koszt z ekspozytora. Schronienie kosztuje 1 tlen. Kopalnia zamiast zasobu wymaga umieszczenia kolonisty z kwater na nowej kopalni; bez dostępnego kolonisty nie można jej zbudować.'),
        h('Legalne położenie'),
        list([
          'Heks musi znajdować się w strefie budowy własnego bota.',
          'Możesz stworzyć lub powiększyć kompleks, kładąc kafel obok budynku tego samego typu i używając odpowiedniej technologii.',
          'Albo zbudować samotny budynek dokładnie 2 heksy od budynku tego typu i bez sąsiedztwa innego budynku tego typu; technologia nie jest wtedy potrzebna.',
          'Własne i cudze schronienia traktuje się jako różne typy budynków.',
        ]),
        h('Rozpatrzenie budowy'),
        list([
          'Połóż kafel zakryty w odpowiedniej orientacji.',
          'Jeżeli rewers ma strzałki, połóż kryształy na wskazanych sąsiednich pustych heksach.',
          'Odwróć kafel.',
          'Otrzymaj zasób odpowiadający nowej wielkości kompleksu; samotny budynek daje 1. Schronienie daje w ten sposób kryształy.',
          'Jeżeli po raz pierwszy utworzyłeś albo powiększyłeś kompleks tego typu, przenieś właściwą kostkę postępu. Schronienia są wyjątkiem.',
          'Jeżeli to budynek LSS, przesuń odpowiadający znacznik toru i sprawdź nagrody oraz wzrost kolonii.',
        ]),
        h('Reguła przemieszczenia'),
        p('Kryształ przykryty budynkiem wraca do zasobu. Kafel odkrycia lub badań wraca do pudełka. Bot albo łazik zostaje przesunięty przez właściciela na najbliższy legalny heks.'),
      ],
    },
    {
      title: 'Ulepszenie budynku',
      content: [
        h('Warunki'),
        list([
          'Musisz posiadać plan odpowiadający typowi budynku.',
          'Wybrany budynek musi znajdować się w strefie własnego bota.',
          'Na wybranym kaflu nie może już znajdować się budynek zaawansowany.',
        ]),
        h('Wykonanie'),
        list([
          'Zapłać koszt ulepszenia pokazany na planie.',
          'Zdejmij znacznik budynku zaawansowanego z planu i połóż go na wybranym budynku.',
          'Od tej chwili akcja wykonawcza planu staje się dostępna.',
          'Jeżeli ulepszenie przykrywa pionek lub kafel, zastosuj regułę przemieszczenia.',
        ]),
        p('Budynek pozostaje tego samego typu dla kompleksów i LSS, ale podczas podróży na orbitę produkuje dla właściciela znacznika zaawansowanego.'),
      ],
    },
    {
      title: 'Przyjęcie statku',
      content: [
        h('Wykonanie'),
        list([
          'Wybierz najniżej położony statek w swoim depozycie.',
          'Przenieś go do hangaru.',
          'Otrzymaj kolonistów i inne korzyści wskazane przy zwolnionym polu.',
          'Nowi koloniści trafiają do kwater, o ile jest miejsce; nadmiar do osobistego zapasu.',
        ]),
        p('Przyjęcie statku zwiększa liczbę dostępnych kolonistów i zwalnia miejsce w depozycie na kryształy. Statek pozostający w hangarze jest wart 3 PS na końcu gry.'),
      ],
    },
    {
      title: 'Zatrudnianie naukowców i kontrakty',
      content: [
        h('Wybór'),
        p('W ramach tej akcji wybierz jedną z dwóch możliwości: zatrudnij dostępnego naukowca albo weź wierzchnią kartę kontraktu ziemskiego.'),
        h('Naukowiec'),
        list([
          'Zapłać koszt widoczny pod kartą naukowca.',
          'Weź kartę i pionek naukowca.',
          'Naukowiec może później zostać skierowany do zgodnego budynku zaawansowanego, także należącego do innego gracza.',
          'Gdy naukowiec pracuje w budynku, jego właściciel i właściciel budynku mogą bezpłatnie wykonywać jego akcję wykonawczą.',
        ]),
        h('Kontrakt'),
        p('Weź wierzchnią kartę kontraktu i połóż przy planszy. Realizuj jej warunek w trakcie gry. Ukończenie lub brak ukończenia wpływa na końcową punktację.'),
      ],
    },
    {
      title: 'Akcje wykonawcze',
      content: [
        h('Zasada ogólna'),
        p('W swojej turze możesz wykonać najwyżej jedną akcję wykonawczą — przed albo po akcji głównej.'),
        h('Źródła akcji'),
        list([
          'Odblokowany plan po ulepszeniu budynku.',
          'Prywatny statek w hangarze.',
          'Inne elementy wyraźnie pokazujące akcję wykonawczą.',
        ]),
        h('Koszt'),
        p('Akcja budynku zaawansowanego zwykle kosztuje 2 kryształy, chyba że korzystasz z odpowiedniego naukowca. Akcje statków mają koszty i efekty pokazane na planszy gracza.'),
        h('Naukowiec'),
        p('Przenieś naukowca do zgodnego planu. Właściciel naukowca i właściciel budynku mogą wykonywać tę akcję bez kosztu kryształów, dopóki naukowiec tam pracuje.'),
      ],
    },
    {
      title: 'Ruch łazika',
      content: [
        h('Punkty ruchu'),
        p('Poziom technologii łazika określa liczbę dostępnych punktów ruchu. Jeden punkt przesuwa łazik o jeden sąsiedni heks.'),
        h('Zbieranie kryształów'),
        p('Kryształ z heksu jest zbierany natychmiast przy przejściu i trafia pod depozyt.'),
        h('Zakończenie ruchu'),
        p('Kafel odkrycia lub badań zdobywa się tylko po zakończeniu ruchu na jego heksie i natychmiast rozpatruje. Łazik musi zakończyć na legalnym heksie zgodnie z limitem jednego elementu.'),
      ],
    },
    {
      title: 'Korzystanie z technologii innych graczy',
      content: [
        h('Kiedy można użyć'),
        p('Jeżeli potrzebujesz technologii do działania, możesz użyć kafla dowolnego gracza o odpowiednim typie i poziomie.'),
        h('Korzyść właściciela'),
        p('Po użyciu cudzej technologii jej właściciel przesuwa kafel o jedno pole w prawo, jeśli ma wolne miejsce, i otrzymuje premię pola. Jeżeli nie można przesunąć kafla, nadal można go użyć, ale właściciel nie otrzymuje przesunięcia.'),
        warning('Właściciel technologii nie może odmówić jej użycia.'),
      ],
    },
    {
      title: 'Rozwój LSS',
      content: [
        h('Ruch znacznika'),
        p('Po zbudowaniu generatora, ekstraktora wody, szklarni albo kondensatora tlenu przesuń odpowiadający znacznik LSS o jedno pole, o ile tor na to pozwala.'),
        h('Nagrody'),
        p('Po wejściu na oznaczone pole rozpatrz nagrodę LSS. W niektórych sytuacjach wybierasz jedną z dostępnych korzyści.'),
        h('Aktualizacja poziomu kolonii'),
        p('Gdy wszystkie cztery znaczniki są powyżej aktualnego poziomu kolonii, wykonaj aktualizację: przesuń znacznik poziomu, przyznaj punktację postępu, uzupełnij elementy wskazane przez nowy poziom i skoryguj tor pozostałych misji.'),
        h('Timing względem misji'),
        list([
          'Najpierw dokończ budowę i ruch LSS oraz ewentualny wzrost poziomu.',
          'Potem sprawdź, czy ta sama budowa spełnia wkład aktywnej misji.',
          'Nie cofaj wzrostu LSS, jeśli misja nie została ukończona — to osobne mechaniki.',
        ]),
      ],
    },
    {
      title: 'Postęp misji',
      content: [
        h('Sprawdzenie po działaniach'),
        p('Po każdym działaniu sprawdź, czy spełniłeś wkład opisany na którejś aktywnej misji. Za każdy wkład otrzymujesz kryształy i przesuwasz kostkę.'),
        h('Ostatni krok'),
        p('Po osiągnięciu końca usuń kartę, a znacznik pozostałych misji przesuń. Jeżeli dotarł na ostatnie pole, koniec gry został uruchomiony, ale bieżąca runda trwa dalej.'),
      ],
    },
    {
      title: 'Koniec rundy',
      content: [
        h('Po ostatniej turze'),
        list([
          'Sprawdź, czy uruchomiono koniec gry.',
          'Rozegraj Fazę Wahadłowca, chyba że jest to ostatnia dodatkowa runda gry.',
          'Podnieś znaczniki graczy, którzy nie podróżowali.',
          'Rozpocznij kolejną rundę według nowej kolejności pól.',
        ]),
      ],
    },
    {
      title: 'Koniec gry',
      content: [
        h('Uruchomienie'),
        p('Na końcu Fazy Kolonizacji sprawdź znacznik pozostałych misji. Może osiągnąć koniec dzięki misjom, poziomowi kolonii albo połączeniu obu.'),
        h('Ostatnie rundy'),
        p('Dokończ bieżącą rundę, łącznie z Fazą Wahadłowca. Następnie rozegraj jeszcze jedną pełną Fazę Kolonizacji. W tej ostatniej rundzie pomiń Fazę Wahadłowca.'),
        p('Kryształy zdobyte w ostatniej rundzie przenieś do depozytu przed punktacją, zgodnie ze zwykłym limitem miejsca.'),
      ],
    },
    {
      title: 'Punktacja końcowa',
      content: [
        h('Tabele punktowania (współdzielone)'),
        p(
          'Poniższe wartości są pełną cyfrową tabelą — nie musisz odczytywać PS z fizycznej planszy gracza. Ta sama tabela zasila Quick Reference, wyszukiwarkę i ekran punktowania.'
        ),
        diagram(
          'scoring-example',
          'Diagram tabel punktowania kwater, technologii i przykładu końcowego',
          'Postęp 1/2/4/7/11. Statki 3. Kwatery rzędy 0/1/3/6/10/15. Technologie kolumny 1/4/9/16/25. Plany ±3/±5. Naukowcy 3 za budynek.'
        ),
        h('Kostki postępu'),
        p('Za 1/2/3/4/5 kostek postępu w obszarze postępu otrzymujesz odpowiednio 1/2/4/7/11 PS.'),
        h('Statki'),
        p('Każdy statek w hangarze daje 3 PS.'),
        h('Kwatery — tabela PS'),
        list([
          'Rząd 1 (2 miejsca, start): 0 PS',
          'Rząd 2 (2 miejsca, start): 1 PS',
          'Rząd 3 (+1 schronienie): 3 PS',
          'Rząd 4 (+2 schronienia): 6 PS',
          'Rząd 5 (+3 schronienia): 10 PS',
          'Rząd 6 (+4 schronienia): 15 PS',
        ]),
        p(
          'Zbierz kolonistów z pól akcji po swojej stronie i ze strefy pracy do kwater, wypełniając miejsca od dołu. Otrzymujesz PS rzędu, w którym stoi twój najwyższy kolonista.'
        ),
        h('Technologie — tabela PS kolumn laboratorium'),
        list([
          'Poziom 1 (lewa kolumna): 1 PS za każdy kafel',
          'Poziom 2: 4 PS za każdy kafel',
          'Poziom 3: 9 PS za każdy kafel',
          'Poziom 4: 16 PS za każdy kafel',
          'Poziom 5 (prawa kolumna): 25 PS za każdy kafel',
        ]),
        h('Plany i budynki zaawansowane'),
        p('Każdy zbudowany plan poziomu 1 daje 3 PS, a poziomu 3 — 5 PS. Za każdy niezbudowany plan tracisz odpowiednio 3 albo 5 PS.'),
        h('Naukowcy'),
        p('Każdy naukowiec daje 3 PS za każdy budynek zaawansowany wskazanego typu znajdujący się na Marsie, niezależnie od właściciela.'),
        h('Kontrakty'),
        p('Dodaj albo odejmij PS zgodnie z kartami. Przed oceną możesz przenieść posiadane zasoby i kryształy na kontrakty.'),
        h('Przykład liczbowy'),
        p(
          'Kostki postępu 4 (= 7), 2 statki (= 6), kwatery na rzędzie 10 PS (= 10), technologie 1+4+4+9 (= 18), plan +3 i −5 (= −2), naukowiec ×2 budynki (= 6), kontrakt +4. Suma kategorii: 7+6+10+18−2+6+4 = 49 PS.'
        ),
        warning(
          'Kontrprzykład: nie punktuj zwykłych budynków bez planu; nie licz statku odrzuconego na podróż; nie pomijaj kar z nieukończonych kontraktów.'
        ),
      ],
    },
    {
      title: 'Remisy',
      content: [
        h('Rozstrzyganie'),
        list([
          'Wygrywa gracz z największą liczbą PS.',
          'Remis rozstrzyga większa liczba kryształów.',
          'Następnie większa liczba własnych budynków zaawansowanych.',
          'Następnie większa liczba własnych kostek w obszarze postępu.',
          'Jeżeli nadal jest remis, gracze dzielą zwycięstwo.',
        ]),
      ],
    },
    {
      title: 'Najczęstsze błędy',
      content: [
        h('Błędy proceduralne'),
        list([
          'Pominięcie obowiązkowej akcji głównej albo wykonanie dwóch akcji wykonawczych.',
          'Wykonanie akcji z niewłaściwej strony planszy.',
          'Wydanie kryształu zdobytego w bieżącej turze.',
          'Pobranie zasobu z magazynu orbitalnego bez ikony magazynu.',
          'Zapomnienie o koszcie zatłoczonego pola akcji.',
          'Pozostawienie dwóch pionków lub znacznika na jednym heksie po zakończeniu tury.',
        ]),
        h('Błędy budowy'),
        list([
          'Budowa poza strefą własnego bota.',
          'Tworzenie kompleksu bez technologii odpowiedniego typu i poziomu.',
          'Przesunięcie kostki postępu za samotny budynek albo schronienie.',
          'Budowa kopalni bez kolonisty w kwaterach.',
          'Pominięcie ruchu LSS po budowie budynku podtrzymywania życia.',
        ]),
        h('Błędy podróży'),
        list([
          'Podróż bez darmowego wahadłowca i bez odrzucenia statku z hangaru.',
          'Pominięcie produkcji podczas podróży na orbitę.',
          'Pominięcie kafla odkrycia podczas podróży na powierzchnię.',
          'Nieodzyskanie kolonistów ze strefy pracy i odpowiedniej strony planszy.',
        ]),
      ],
    },
    {
      title: 'Księga odniesienia i FAQ',
      content: [
        h('Pola kolejności'),
        list([
          'Pole 1 i 5: brak premii.',
          'Pole 2: weź 1 kryształ albo 1 zasób z magazynu orbitalnego.',
          'Pole 3: weź 1 kafel technologii zgodnie ze zwykłymi zasadami i zapłać koszt rzędu.',
          'Pole 4: zapłać 1 wodę albo 1 roślinę, aby przenieść 1 kolonistę z osobistego zapasu do kwater.',
          'Pole 6: weź 1 kryształ albo 1 zasób z magazynu orbitalnego.',
          'Pole 7: wykorzystaj do 2 punktów ruchu botów. Za kryształy możesz dokupić dalsze punkty ruchu.',
          'Pole 8: weź 1 odkryty plan zgodnie ze zwykłymi zasadami.',
        ]),
        h('Technologie — komplet działania'),
        list([
          'Szybszy łazik: podczas ruchu łazika dodaj liczbę punktów ruchu nie większą niż poziom tej technologii.',
          'Większa pojemność statku: podczas Przyjęcia statku otrzymaj dodatkowych kolonistów w liczbie nie większej niż poziom technologii.',
          'Efektywne ulepszanie: podczas akcji Ulepszenia ulepsz dodatkowe budynki w liczbie nie większej niż poziom technologii; każdy plan nadal jest jednorazowy.',
          'Technologie sześciu rodzajów budynków: pozwalają tworzyć albo powiększać kompleks odpowiedniego typu. Poziom użytej technologii musi być co najmniej równy liczbie budynków tego typu, które nowy kafel połączy w jeden kompleks.',
          'Technologia należy do właściciela, ale może użyć jej każdy. Po użyciu cudzej technologii właściciel otrzymuje korzyść nadrukowaną przy jej symbolu: tlen albo rozwinięcie technologii, zależnie od kafla.',
        ]),
        h('Nagrody LSS'),
        p('Gdy budujesz budynek LSS, którego znacznik przed budową znajduje się poniżej aktualnego poziomu kolonii, otrzymujesz dwie korzyści: nagrodę z kafla nad daną kolumną oraz jedną z korzyści pokazanych w lewym górnym rogu obszaru LSS. Gdy znacznik nie był poniżej poziomu kolonii, nagrody nie otrzymujesz.'),
        list([
          'PS równy poziomowi twojej najbardziej rozwiniętej technologii.',
          '2 PS za każdy statek w hangarze.',
          '2 PS za każdy własny znacznik budynku zaawansowanego na Marsie.',
          '2 PS za każdą kopalnię zawierającą twojego kolonistę albo znacznik budynku zaawansowanego.',
          '2 PS za każdego własnego bota na Marsie.',
          '2 PS za każdy kafel odkrycia zabrany przez twój łazik.',
          '2 PS za każde własne schronienie na Marsie.',
          '1 PS za każdego kolonistę w kwaterach.',
          'Porusz boty, korzystając z maksymalnie 2 punktów ruchu.',
          'Otrzymaj 1 minerał.',
          'Otrzymaj 1 kryształ.',
          'Odzyskaj 1 kolonistę z dowolnego pola akcji albo strefy pracy.',
        ]),
        h('Kafle badań i odkryć'),
        p('Kafel badań leży na mapie od przygotowania. Kafel odkrycia pojawia się w trakcie gry. Łazik zabiera taki kafel wyłącznie wtedy, gdy kończy na jego heksie. Efekt rozpatruje się natychmiast. Jeżeli efekt wymaga kosztu, którego nie możesz zapłacić, nie możesz zakończyć ruchu na tym heksie.'),
        list([
          'Rozwiń jedną technologię dwa razy albo dwie technologie po jednym razie bez kosztu.',
          'Zbuduj wskazany rodzaj budynku; kompleks wymaga technologii.',
          'Zbuduj dowolny budynek; kompleks wymaga technologii.',
          'Otrzymaj wskazaną liczbę zasobów.',
          'Rozwiń jedną technologię dwa razy albo dwie po jednym razie.',
          'Weź do 2 planów.',
          'Weź 1 technologię, płacąc zwykły koszt.',
          'Ulepsz do 2 budynków.',
          'Otrzymaj 3 kryształy.',
          'Zatrudnij naukowca albo weź kontrakt.',
          'Weź do 2 zasobów lub kryształów z magazynu orbitalnego.',
        ]),
        h('Naukowcy — koszt, miejsce pracy i punktacja'),
        list([
          'Geolog: koszt — przenieś 2 kolonistów z kwater do strefy pracy. Pracuje w ulepszonej kopalni. Na końcu daje 3 PS za każdy ulepszony generator na Marsie.',
          'Inżynier badań i rozwoju: koszt 2 minerały. Pracuje w ulepszonym generatorze. Daje 3 PS za każdy ulepszony ekstraktor wody.',
          'Hydrolog: koszt 2 baterie. Pracuje w ulepszonym ekstraktorze wody. Daje 3 PS za każdą ulepszoną szklarnię.',
          'Biochemik: koszt 2 wody. Pracuje w ulepszonej szklarni. Daje 3 PS za każdy ulepszony kondensator tlenu.',
          'Geochemik: koszt 2 rośliny. Pracuje w ulepszonym kondensatorze tlenu. Daje 3 PS za każde ulepszone schronienie.',
          'Inżynier systemów: koszt 2 tlenu. Pracuje w ulepszonym schronieniu. Daje 3 PS za każdą ulepszoną kopalnię.',
        ]),
        p('Naukowiec pracujący w zgodnym budynku zaawansowanym pozwala właścicielowi naukowca i właścicielowi budynku wykonywać akcję tego budynku bez płacenia 2 kryształów.'),
        h('Kontrakty ziemskie'),
        p('Kontrakty pojawiają się po zabraniu pierwszego naukowca i kosztują 1 kryształ. Występują dwa rodzaje:'),
        list([
          'Ulepszenie: na końcu gry musisz mieć własny znacznik budynku zaawansowanego na kompleksie wskazanego typu o wielkości co najmniej 4. Sukces: 12 PS. Porażka: −6 PS.',
          'Dostawa: podczas gry możesz trwale odkładać na karcie wskazane elementy. Nie liczą się do limitu magazynu i nie można ich odzyskać. Sukces: 9 PS. Porażka: −4 PS. Minerał nie zastępuje innego zasobu.',
        ]),
        h('Plany poziomu 1 — wszystkie 12'),
        list([
          '1. Plac budowy — kopalnia; po zdobyciu minerał; geolog. Akcja: ulepsz 1 budynek; każdy kolonista wysłany do pracy pozwala ulepszyć jeszcze 1.',
          '2. Złoże metalu — kopalnia; minerał; geolog. Akcja: zbuduj kopalnię. Kompleks wymaga technologii; każdy kolonista wysłany do pracy daje 1 chwilowy poziom technologii.',
          '3. Zautomatyzowana produkcja — generator; bateria; inżynier B+R. Akcja: każda twoja kopalnia i każdy własny budynek zaawansowany produkuje właściwy element; schronienie produkuje kryształ. Każdy wysłany kolonista pozwala jednemu z nich wyprodukować ponownie.',
          '4. Turbiny wiatrowe — generator; bateria; inżynier B+R. Akcja: zbuduj generator; kompleks wymaga technologii, a koloniści mogą ją chwilowo podnosić.',
          '5. Prywatny statek — ekstraktor wody; woda; hydrolog. Akcja: Przyjmij statek za 1 roślinę i 1 wodę, otrzymując 2 kolonistów i 1 bota albo 3 kolonistów. Każdy wysłany kolonista pozwala przyjąć kolejny statek.',
          '6. Odparowywacz wilgoci — ekstraktor wody; woda; hydrolog. Akcja: zbuduj ekstraktor wody; kompleks wymaga technologii.',
          '7. Biomarket — szklarnia; roślina; biochemik. Akcja: weź 1 zasób albo kryształ z magazynu; każdy wysłany kolonista pozwala wziąć kolejny.',
          '8. Farma hydroponiczna — szklarnia; roślina; biochemik. Akcja: zbuduj szklarnię; kompleks wymaga technologii.',
          '9. Zbiornik tlenu — kondensator tlenu; tlen; geochemik. Akcja: weź 1 technologię z siatki; każdy wysłany kolonista pozwala wziąć następną.',
          '10. Koncentrator — kondensator tlenu; tlen; geochemik. Akcja: zbuduj kondensator tlenu; kompleks wymaga technologii.',
          '11. Kasyno — schronienie; kryształ; inżynier systemów. Akcja: otrzymaj 2 kryształy; każdy wysłany kolonista daje dodatkowy kryształ.',
          '12. Siłownia — schronienie; kryształ; inżynier systemów. Akcja: zbuduj schronienie; kompleks wymaga technologii.',
        ]),
        p('Każdy plan poziomu 1 ulepsza zgodny budynek dowolnej wielkości. Zbudowany daje 3 PS; niezbudowany powoduje −3 PS.'),
        h('Plany poziomu 3 — wszystkie 12'),
        list([
          '13. Kopalnia minerałów — kopalnia; minerał; geolog. Akcja: zdobądź 2 PS.',
          '14. Biolaboratorium — kopalnia; minerał; geolog. Akcja: weź kontrakt, płacąc jego koszt.',
          '15. Radar — generator; bateria; inżynier B+R. Przy pierwszym użyciu połóż na karcie odkryty kafel odkrycia, uzupełnij ekspozycję i rozpatrz efekt. Później akcja powtarza efekt zapisanego kafla.',
          '16. Dron budowlany AI600 — generator; bateria; inżynier B+R. Akcja: ulepsz budynek w dowolnym miejscu mapy; strefa botów jest dla tej akcji nieograniczona.',
          '17. Laboratorium badawcze — ekstraktor wody; woda; hydrolog. Akcja: rozwiń jedną technologię do 2 razy albo dwie po 1 razie, płacąc normalnie; każdy wysłany kolonista daje dodatkowe rozwinięcie.',
          '18. Akwedukt — ekstraktor wody; woda; hydrolog. Akcja: przenieś do 2 kolonistów ze strefy pracy do kwater.',
          '19. Ekologiczny kurort — szklarnia; roślina; biochemik. Akcja: odzyskaj dowolną liczbę własnych kolonistów z pól akcji po obu stronach planszy do kwater.',
          '20. Targ handlowy — szklarnia; roślina; biochemik. Akcja: wydaj 1 zasób z magazynu i weź dowolne 2 zasoby inne niż minerały z zasobu ogólnego; każdy wysłany kolonista daje 1 dodatkowy zasób.',
          '21. Boty recyklingowe — kondensator tlenu; tlen; geochemik. Akcja: porusz boty do 2 punktów. W tym ruchu traktuj je jak łaziki: mogą zbierać kryształy, badania i odkrycia.',
          '22. Winda orbitalna — kondensator tlenu; tlen; geochemik. Akcja: podróżuj na orbitę, wykonując zwykłe kroki. Jeżeli wykonasz ją przed akcją główną, akcję główną wykonujesz już na orbicie.',
          '23. Biblioteka — schronienie; kryształ; inżynier systemów. Akcja: weź plan; każdy wysłany kolonista pozwala wziąć kolejny.',
          '24. Centrum dowodzenia — schronienie; kryształ; inżynier systemów. Akcja: porusz łazik o liczbę pól równą najwyższemu poziomowi technologii łazika obecnemu w grze; każdy kryształ daje dodatkowy ruch.',
        ]),
        p('Plan poziomu 3 może ulepszyć zgodny budynek tylko w kompleksie wielkości co najmniej 3. Zbudowany daje 5 PS; niezbudowany powoduje −5 PS.'),
        h('Cele prywatne — wszystkie 16 warunków'),
        list([
          'Posiadaj 4 plany, zbudowane lub nie.',
          'Posiadaj 3 schronienia.',
          'Posiadaj 3 kopalnie z własnym kolonistą albo budynkiem zaawansowanym.',
          'Posiadaj 3 zebrane kafle odkryć.',
          'Posiadaj znacznik budynku zaawansowanego na kompleksie wielkości co najmniej 4.',
          'Posiadaj 4 jednakowe zasoby inne niż minerały.',
          'Miej 3 własne budynki zaawansowane.',
          'Kolonia osiągnęła poziom 3.',
          'Miej 7 kolonistów w kwaterach.',
          'Posiadaj co najmniej 5 technologii; początkowa technologia schronienia się liczy.',
          'Posiadaj 3 statki w hangarze.',
          'Posiadaj 2 kafle badań.',
          'Posiadaj 1 naukowca i ukończony kontrakt.',
          'Miej 3 technologie na wskazanych polach laboratorium.',
          'Miej 3 kostki w obszarze postępu.',
          'Miej 2 budynki zaawansowane w dwóch różnych kompleksach wielkości co najmniej 3.',
        ]),
        p('Typowe nagrody celów: dwa darmowe rozwinięcia technologii albo alternatywna premia wskazana na karcie, np. ulepszenia, koloniści, zasoby, ruch łazika, darmowy statek, naukowiec, kontrakt, kryształy lub odzyskanie kolonistów. W całej grze realizujesz najwyżej jeden cel.'),
        h('Misje — wymagania dla 2/3/4 graczy'),
        list([
          'Zebrane kafle badań: 5/5/7. Nagroda za wkład: 2 kryształy.',
          'Wzięte plany: 10/10/13. Nagroda: 1 kryształ.',
          'Budynki zaawansowane na mapie: 8/8/10. Nagroda: 1 kryształ.',
          'Dodatkowe boty umieszczone na mapie, bez botów startowych: 3/4/5. Nagroda: 2 kryształy.',
          'Kafle odkryć zebrane przez łaziki: 5/7/9. Nagroda: 1 kryształ.',
          'Technologie wzięte z siatki: 7/11/12. Nagroda: 1 kryształ.',
          'Kostki w obszarze postępu: 8/11/14. Nagroda: 1 kryształ.',
          'Zatrudnieni naukowcy: 4/4/5. Nagroda: 2 kryształy.',
          'Ukończone kontrakty: 2/2/3. Nagroda: 2 kryształy.',
        ]),
        h('Aktualizacja poziomu kolonii — pełna sekwencja'),
        list([
          'Za każdym wzrostem: przesuń znacznik poziomu o jeden rząd, uzupełnij puste miejsca siatki technologii, uzupełnij magazyn orbitalny i udostępnij każdemu graczowi kolejny statek.',
          'Poziom 2: zapunktuj obszar postępu; wahadłowiec po podróży wraca na pole 2; usuń wszystkie plany z ekspozycji i wyłóż 12 nowych kart ze stosu.',
          'Poziom 3: zapunktuj postęp; zmniejsz liczbę potrzebnych misji o 1; wahadłowiec wraca na pole 3; usuń z ekspozycji wszystkie plany poziomu 1 i wyłóż pozostałe plany poziomu 3.',
          'Poziom 4: nie punktuj postępu; ponownie zmniejsz liczbę potrzebnych misji o 1; wahadłowiec przestaje się przesuwać.',
          'Poziom 5: nie punktuj postępu; uruchom koniec gry; wahadłowiec podróżuje jeszcze raz w najbliższej Fazie Wahadłowca, po czym usuń je z gry.',
          'Dalszy wzrost ponad poziom 5 nie wywołuje efektu.',
        ]),
        h('Akcje wykonawcze nadrukowane na planszy gracza'),
        list([
          '4 kryształy: rozwiń technologię raz bez kosztu.',
          '4 kryształy: porusz łazik do 2 pól; technologia i kryształy mogą zwiększyć ruch.',
          '3 kryształy: ulepsz 1 budynek.',
          '3 kryształy: weź 1 plan.',
          '3 kryształy: otrzymaj 1 minerał.',
          '2 kryształy: porusz boty, używając do 2 punktów ruchu; kryształy mogą zwiększyć ruch.',
          '2 kryształy: weź 1 zasób z magazynu orbitalnego.',
          '2 kryształy albo zgodny naukowiec: wykonaj akcję budynku zaawansowanego.',
        ]),
        h('Wariant Pierwsi Koloniści'),
        p('Wariant Pierwsi Koloniści jest zalecany przez instrukcję jako urozmaicenie rozgrywki. Każdy kafel Pierwszego Kolonisty ma odpowiadającą mu parę kart. Podczas przygotowania wybierz losowo odpowiednie zestawy zgodnie z liczbą graczy i rozdaj graczom początkowe zdolności zgodnie z kartami. Efekty tych kart mają pierwszeństwo przed zasadami ogólnymi tylko w zakresie, który wyraźnie zmieniają.'),
        warning('Karty Pierwszych Kolonistów zawierają własną ikonografię i dokładne warunki. Przed rozpoczęciem partii każdy gracz powinien przeczytać na głos swoją kartę. Nie łącz dwóch kart przypisanych do tego samego kafla w jednym zestawie startowym, chyba że przygotowanie wariantu wyraźnie na to pozwala.'),
        h('FAQ — przebieg gry'),
        p('Czy muszę wykonać akcję główną? Tak. W każdej turze wykonujesz dokładnie jedną akcję główną. Akcja wykonawcza jest opcjonalna i najwyżej jedna.'),
        p('Czy akcję wykonawczą można wykonać przed akcją główną? Tak, przed albo po niej. Nie można rozdzielić jednej akcji wykonawczej na dwa momenty.'),
        p('Czy mogę wykonać główną akcję z drugiej strony planszy? Nie, chyba że konkretny efekt przeniósł cię przed wykonaniem akcji głównej, np. Winda orbitalna lub nagroda celu.'),
        p('Czy minerał jest jokerem? Zastępuje podstawowy zasób, ale nigdy kryształ i nigdy brakujący typ zasobu na kontrakcie dostawy.'),
        p('Czy nowo zdobyte kryształy są dostępne natychmiast? Nie. Trafiają pod depozyt i stają się dostępne na początku twojej następnej tury.'),
        h('FAQ — budowa i mapa'),
        p('Czy budynek można zbudować bez własnego bota? Nie. Miejsce musi leżeć na heksie bota albo heksie sąsiednim.'),
        p('Czy samotny budynek wymaga technologii? Nie. Musi jednak leżeć dokładnie dwa heksy od najbliższego budynku tego samego typu i nie może z nim sąsiadować.'),
        p('Kiedy przesuwam kostkę postępu? Gdy pierwszy raz tworzysz albo powiększasz kompleks danego typu. Nie za samotny budynek i nie za schronienie.'),
        p('Co się dzieje z elementem przykrytym budynkiem? Kryształ wraca do zasobu, kafel badań lub odkrycia do pudełka, a bot lub łazik zostaje przesunięty na najbliższy legalny heks.'),
        h('FAQ — podróż'),
        p('Czy można podróżować, gdy wahadłowiec tylko się przesuwa? Tak, odrzucając statek z hangaru.'),
        p('Czy można nie lecieć z wahadłowcem? Tak. Podnieś swój znacznik i pozostań po tej samej stronie.'),
        p('Których kolonistów odzyskuję lecąc na powierzchnię? Kolonistów z pól akcji orbity (strony, którą opuszczasz) oraz ze strefy pracy. Lecąc na orbitę odzyskujesz kolonistów z pól akcji kolonii i ze strefy pracy.'),
        p('Czy produkcja następuje przy podróży na powierzchnię? Nie. Produkcja jest pierwszym krokiem podróży na orbitę.'),
        h('FAQ — koniec gry'),
        p('Czy gra kończy się natychmiast po ukończeniu wymaganej misji? Nie. Dokończ bieżącą rundę i rozegraj jeszcze jedną rundę. W ostatniej rundzie nie ma Fazy Wahadłowca.'),
        p('Czy podczas końcowej punktacji odzyskuję wszystkich kolonistów? Odzyskujesz kolonistów ze strony planszy, na której znajduje się twój znacznik, oraz ze strefy pracy.'),
        warning('Ta księga odniesienia opisuje wszystkie stałe elementy gry podstawowej. Konkretne kafle badań i odkryć należy zawsze rozpatrywać według ich ikon; rozdział podaje pełne rodzaje efektów, ale grafika kafla wskazuje dokładną wartość i koszt.'),
      ],
    },
    {
      title: 'Pełny skrót tury',
      content: [
        h('Początek'),
        list([
          'Przenieś kryształy spod depozytu do wolnych miejsc.',
          'Sprawdź stronę planszy i kolejność.',
        ]),
        h('Tura'),
        list([
          'Opcjonalnie wykonaj akcję wykonawczą.',
          'Wykonaj dokładnie jedną akcję główną.',
          'Opcjonalnie wykonaj akcję wykonawczą, jeżeli nie zrobiłeś tego wcześniej.',
          'Sprawdź misje i ewentualny cel prywatny.',
          'Połóż znacznik gracza.',
        ]),
        h('Po wszystkich turach'),
        list([
          'Przesuń wahadłowiec o jedno pole; jeżeli jest przy środku, przemieść je na drugą stronę na pole wynikające z poziomu kolonii.',
          'W kolejności każdy decyduje o podróży.',
          'Darmowo podróżują gracze stojący po stronie, z której odleciało wahadłowiec; pozostali podróżują po odrzuceniu statku.',
          'Rozpatrz odpowiednie kroki podróży i wybierz nowe pole kolejności.',
        ]),
        h('Przykład narracyjny pełnej rundy (3 gracze)'),
        p(
          'Kolejność: Anna (orbita), Bartek (orbita), Celina (kolonia). Anna przenosi kryształy spod depozytu, opcjonalnie wykonuje akcję wykonawczą ze statku, potem bierze plan (akcja główna orbitalna), sprawdza misję i kładzie znacznik. Bartek buduje technologię. Celina na powierzchni wykonuje Centrum Kontroli, potem budowę generatora — przesuwa LSS i sprawdza wzrost.'
        ),
        p(
          'Faza Wahadłowca: wahadłowiec odlatuje z orbity na kolonię. Anna i Bartek mogą lecieć darmowo (opuszczają orbitę): eksploracja, odzyskanie kolonistów z orbity, nowe pole kolejności kolonii. Celina może pozostać albo lecieć na orbitę po odrzuceniu statku: produkcja, odzyskanie kolonistów z kolonii, pole kolejności orbity. Kolejna runda zaczyna się od nowych pól kolejności.'
        ),
        tip(
          'Zapamiętaj: przy każdej podróży odzyskujesz kolonistów ze strony, którą opuszczasz — nigdy ze strony docelowej.'
        ),
      ],
    },
  ],

  firstGameSteps: [
    {
      id: 1,
      title: 'Wybierz długość i misje',
      content: [
        'Do pierwszej partii wybierz grę krótką: 2 krótkie i 1 długą misję.',
        'Ustaw kostki wymagań na polach dla 3 albo 4 graczy.',
        'Rozważ użycie wariantu Pierwsi Koloniści, jeżeli masz jego komponenty przygotowane.',
      ],
      why: 'Misje określają tempo i moment uruchomienia końca gry.',
      commonMistake: 'Nie ustawiaj kostek na wartościach przeznaczonych dla innej liczby graczy.',
      chapterIndex: 15,
    },
    {
      id: 2,
      title: 'Rozłóż orbitę',
      content: [
        'Przygotuj odkrycia, magazyn, technologie i sześć odkrytych planów.',
        'Sprawdź, czy w magazynie jest po 3 elementy każdego rodzaju.',
      ],
      why: 'Orbita dostarcza planów, technologii i zaopatrzenia potrzebnego na początku gry.',
      commonMistake: 'Nie uzupełniaj siatki technologii ani planów po każdej akcji — robi się to przy wzroście kolonii.',
      chapterIndex: 3,
    },
    {
      id: 3,
      title: 'Rozłóż powierzchnię',
      content: [
        'Umieść budynki startowe i ekspozytor budynków.',
        'Przygotuj LSS, nagrody, obszar postępu i kafle badań.',
      ],
      why: 'Prawidłowa orientacja budynków startowych jest potrzebna do poprawnego układania kolejnych kafli.',
      commonMistake: 'Nie obracaj początkowych kafli w różnych kierunkach.',
      chapterIndex: 5,
    },
    {
      id: 4,
      title: 'Przygotuj plansze graczy',
      content: [
        'Połóż zasoby, kryształ, statki, technologię schronienia i kostki postępu.',
        'Umieść 3 kolonistów w kwaterach.',
        'Ustaw początkowe schronienie i bota na mapie.',
      ],
      why: 'Plansza gracza pokazuje limity zasobów, kwater, depozytu i dostępne komponenty.',
      commonMistake: 'Nie wkładaj wszystkich kolonistów do kwater — zaczynasz tylko z trzema.',
      chapterIndex: 3,
    },
    {
      id: 5,
      title: 'Wybierz kolejność i położenie wahadłowca',
      content: [
        'Każdy wybiera wolne pole kolejności i może otrzymać jego premię.',
        'Gracz na najwyższym numerze wybiera początkową stronę wahadłowca.',
      ],
      why: 'Kolejność wpływa na pierwszą rundę i na to, z której strony zaczną gracze.',
      commonMistake: 'Pole kolejności może mieć koszt; premię otrzymujesz tylko po jego opłaceniu.',
      chapterIndex: 18,
    },
    {
      id: 6,
      title: 'Poznaj strukturę rundy',
      content: [
        'Najpierw każdy wykonuje jedną turę w kolejności.',
        'Następnie rozpatruje się Fazę Wahadłowca.',
      ],
      why: 'ON MARS nie ma klasycznych rund akcji aż do wyczerpania pracowników — każdy ma dokładnie jedną turę na rundę.',
      commonMistake: 'Nie wykonuj drugiej tury przed Fazą Wahadłowca.',
      chapterIndex: 18,
    },
    {
      id: 7,
      title: 'Wykonaj pierwszą turę na orbicie',
      content: [
        'Wybierz jedną dostępną akcję główną.',
        'Na pierwszą partię najłatwiejsze są zaopatrzenie, technologia albo plan.',
        'Opcjonalnie wykonaj jedną akcję wykonawczą, jeśli masz dostępną.',
      ],
      why: 'Orbita przygotowuje cię do skutecznej budowy na powierzchni.',
      commonMistake: 'Nie można wykonać dwóch akcji głównych.',
      chapterIndex: 19,
    },
    {
      id: 8,
      title: 'Obsłuż pole z czerwonym kolonistą',
      content: [
        'Umieść kolonistę z kwater na polu akcji.',
        'Jeżeli są tam inne kolory, opłać koszt kryształami lub kolonistami przeniesionymi do strefy pracy.',
      ],
      why: 'Zatłoczenie sprawia, że popularne akcje stają się droższe.',
      commonMistake: 'Koszt zależy od innych kolorów obecnych na akcji, a nie od liczby wszystkich pionków w grze 3–4 osobowej.',
      chapterIndex: 8,
    },
    {
      id: 9,
      title: 'Wzmocnij akcję',
      content: [
        'Przy turkusowej ikonie możesz przenieść kolonistów do strefy pracy.',
        'Każdy wzmacnia akcję zgodnie z jej opisem.',
      ],
      why: 'Wzmocnienie pozwala pobrać więcej elementów albo zwiększyć chwilowy poziom technologii.',
      commonMistake: 'Kolonista w strefie pracy nie wraca od razu; odzyskasz go podczas podróży.',
      chapterIndex: 8,
    },
    {
      id: 10,
      title: 'Rozpatrz Fazę Wahadłowca',
      content: [
        'Przesuń wahadłowiec o jedno pole ku środkowi albo wykonaj jego przelot.',
        'Każdy w kolejności wybiera: podróż albo pozostanie.',
      ],
      why: 'To główny sposób zmiany zestawu dostępnych akcji.',
      commonMistake: 'Możesz podróżować nawet bez przelotu wahadłowca, ale kosztuje to statek z hangaru.',
      chapterIndex: 37,
    },
    {
      id: 11,
      title: 'Podróż na powierzchnię',
      content: [
        'Umieść kafel odkrycia dokładnie 3 pola od łazika lub początkowej kopalni.',
        'Odzyskaj kolonistów z orbity i strefy pracy.',
        'Wybierz wolne pole kolejności kolonii.',
      ],
      why: 'Podróż odświeża kolonistów i umożliwia budowę.',
      commonMistake: 'Kafel odkrycia musi znaleźć się dokładnie 3 heksy od punktu odniesienia.',
      chapterIndex: 20,
    },
    {
      id: 12,
      title: 'Przesuń bota',
      content: [
        'Wykonaj Centrum Kontroli albo odpowiednią akcję wykonawczą.',
        'Ustaw bota tak, aby planowany heks budowy był w jego strefie.',
      ],
      why: 'Bez własnego bota obejmującego miejsce nie można budować ani ulepszać.',
      commonMistake: 'Strefa obejmuje heks bota i wszystkie heksy sąsiednie, nie obszar w promieniu dwóch pól.',
      chapterIndex: 9,
    },
    {
      id: 13,
      title: 'Zbuduj samotny budynek',
      content: [
        'Wybierz heks dokładnie 2 pola od budynku tego samego typu.',
        'Nie kładź go obok innego budynku tego typu.',
        'Zapłać koszt i rozpatrz zasób, kryształy oraz LSS.',
      ],
      why: 'Samotny budynek nie wymaga technologii i jest prostym pierwszym krokiem.',
      commonMistake: 'Samotny budynek nie przesuwa kostki postępu.',
      chapterIndex: 28,
    },
    {
      id: 14,
      title: 'Zbuduj kompleks',
      content: [
        'Wybierz miejsce sąsiadujące z budynkiem tego samego typu.',
        'Użyj technologii o poziomie co najmniej równym liczbie budynków, z którymi połączysz nowy kafel.',
        'Przesuń kostkę postępu, jeżeli pierwszy raz tworzysz lub powiększasz kompleks tego typu.',
      ],
      why: 'Kompleks daje więcej zasobów przy budowie i rozwija obszar postępu.',
      commonMistake: 'Liczą się wszystkie budynki połączonego kompleksu, a nie tylko bezpośredni sąsiad nowego kafla.',
      chapterIndex: 13,
    },
    {
      id: 15,
      title: 'Przesuń LSS',
      content: [
        'Po budowie generatora, wody, szklarni albo tlenu przesuń właściwy znacznik.',
        'Rozpatrz nagrodę i sprawdź, czy wszystkie tory przekroczyły poziom kolonii.',
      ],
      why: 'LSS steruje tempem rozwoju i końcem gry.',
      commonMistake: 'Kopalnie i schronienia nie przesuwają torów LSS.',
      chapterIndex: 35,
    },
    {
      id: 16,
      title: 'Pozyskaj i użyj planu',
      content: [
        'Na orbicie weź plan i połóż na nim znacznik budynku zaawansowanego.',
        'Na powierzchni ulepsz zgodny budynek, płacąc koszt.',
        'Po ulepszeniu odblokuj akcję wykonawczą planu.',
      ],
      why: 'Plany dają punkty, produkcję i dodatkowe akcje.',
      commonMistake: 'Akcja wykonawcza planu jest zablokowana, dopóki znacznik leży na karcie.',
      chapterIndex: 24,
    },
    {
      id: 17,
      title: 'Przyjmij statek',
      content: [
        'Przenieś najniższy statek z depozytu do hangaru.',
        'Otrzymaj kolonistów i premie zwolnionego pola.',
      ],
      why: 'Zyskujesz ludzi, miejsce na kryształy i końcowe PS.',
      commonMistake: 'Statek użyty do płatnej podróży jest usuwany z gry i nie zapunktuje.',
      chapterIndex: 30,
    },
    {
      id: 18,
      title: 'Użyj łazika',
      content: [
        'Przesuwaj go według technologii.',
        'Zbieraj kryształy po drodze.',
        'Zakończ na kaflu badań lub odkrycia, aby go zdobyć.',
      ],
      why: 'Eksploracja daje premie, cele i elementy potrzebne do misji.',
      commonMistake: 'Samo przejście przez kafel badań lub odkrycia nie wystarcza.',
      chapterIndex: 33,
    },
    {
      id: 19,
      title: 'Zatrudnij naukowca albo weź kontrakt',
      content: [
        'Wybierz jedną z dwóch opcji akcji.',
        'Naukowca kieruj później do zgodnego budynku zaawansowanego.',
        'Na kontraktach odkładaj dokładnie wymagane elementy.',
      ],
      why: 'Naukowcy ułatwiają akcje wykonawcze, a kontrakty są ważnym źródłem końcowych PS.',
      commonMistake: 'Minerał nie zastępuje zasobu dostarczanego na kontrakt.',
      chapterIndex: 31,
    },
    {
      id: 20,
      title: 'Podróż na orbitę i produkcja',
      content: [
        'Wyprodukuj zasoby z budynków zaawansowanych i minerały z kopalń.',
        'Odzyskaj kolonistów z powierzchni i strefy pracy.',
        'Wybierz pole kolejności orbity.',
      ],
      why: 'Produkcja jest jednym z głównych powodów powrotu na orbitę.',
      commonMistake: 'Zwykłe budynki nie produkują podczas podróży; produkują budynki zaawansowane oraz kopalnie z kolonistami lub znacznikami.',
      chapterIndex: 22,
    },
    {
      id: 21,
      title: 'Kontroluj misje',
      content: [
        'Po każdej akcji sprawdź wkład w aktywne misje.',
        'Odbierz kryształy i przesuń kostkę.',
        'Po ukończeniu usuń misję i przesuń znacznik pozostałych misji.',
      ],
      why: 'Misje określają długość partii.',
      commonMistake: 'Kryształy z misji trafiają pod depozyt i nie są dostępne od razu.',
      chapterIndex: 36,
    },
    {
      id: 22,
      title: 'Zrealizuj cel prywatny',
      content: [
        'Gdy spełniasz warunek, ujawnij cel i wybierz nagrodę.',
        'W całej grze zrealizujesz najwyżej jeden cel.',
        'Pozostałe możesz odrzucać jako jednorazowy kryształ.',
      ],
      why: 'Cel daje elastyczną premię i kierunek działania.',
      commonMistake: 'Użycie karty jako kryształu nie daje fizycznego znacznika.',
      chapterIndex: 16,
    },
    {
      id: 23,
      title: 'Rozpoznaj uruchomienie końca',
      content: [
        'Na końcu Fazy Kolonizacji sprawdź tor pozostałych misji.',
        'Dokończ bieżącą rundę i rozegraj jeszcze jedną rundę kolonizacji.',
      ],
      why: 'Po uruchomieniu końca nadal masz czas na ostatnie przygotowania punktacji.',
      commonMistake: 'Gra nie kończy się natychmiast po ukończeniu ostatniej wymaganej misji.',
      chapterIndex: 38,
    },
    {
      id: 24,
      title: 'Wykonaj punktację końcową',
      content: [
        'Policz postęp, statki, kolonistów, technologie, plany, naukowców i kontrakty.',
        'Rozstrzygnij ewentualny remis według kryształów, budynków zaawansowanych i postępu.',
      ],
      why: 'Duża część wyniku powstaje dopiero podczas końcowej punktacji.',
      commonMistake: 'Nie zapomnij o karach za niezbudowane plany i nieukończone kontrakty.',
      chapterIndex: 39,
    },
  ],
};

export const onMarsQuickReference = [
  {
    id: 'turn-order',
    title: 'Kolejność tury',
    chapterIndex: 18,
    text: 'Początek: przenieś nowe kryształy. Następnie 1 akcja główna oraz opcjonalnie 1 akcja wykonawcza przed albo po niej. Sprawdź misje i połóż znacznik.',
  },
  {
    id: 'orbit-actions',
    title: 'Akcje na orbicie',
    chapterIndex: 23,
    text: 'Kapsuła desantowa, plan, nowa technologia, badania i rozwój, zaopatrzenie.',
  },
  {
    id: 'surface-actions',
    title: 'Akcje na powierzchni',
    chapterIndex: 27,
    text: 'Centrum Kontroli, budowa, ulepszenie, przyjęcie statku, naukowiec lub kontrakt.',
  },
  {
    id: 'executive-actions',
    title: 'Akcje wykonawcze',
    chapterIndex: 32,
    text: 'Najwyżej 1 na turę, przed albo po głównej. Źródłem są odblokowane plany, statki i inne oznaczone elementy.',
  },
  {
    id: 'travel',
    title: 'Podróż',
    chapterIndex: 20,
    text: 'Darmowo z wahadłowcem z opuszczanej strony; inaczej odrzuć statek. Odzyskujesz kolonistów ze strony, którą opuszczasz (nie docelowej). Na powierzchnię: odkrycie + koloniści z orbity. Na orbitę: produkcja + koloniści z kolonii.',
  },
  {
    id: 'buildings',
    title: 'Budynki i kompleksy',
    chapterIndex: 13,
    text: 'Buduj w strefie bota. Samotny: dokładnie 2 heksy, bez sąsiedztwa tego samego typu, bez technologii. Kompleks: sąsiedztwo tego samego typu + technologia o poziomie ≥ liczbie łączonych budynków.',
  },
  {
    id: 'technology',
    title: 'Technologia',
    chapterIndex: 12,
    text: 'Poziom określa kolumna laboratorium. Można używać cudzych kafli; właściciel rozwija użyty kafel o jedno pole, jeśli może.',
  },
  {
    id: 'rover',
    title: 'Łazik',
    chapterIndex: 33,
    text: 'Kryształy zbiera przy przejściu. Kafel badań lub odkrycia zdobywa tylko po zakończeniu ruchu na nim.',
  },
  {
    id: 'lss',
    title: 'LSS',
    chapterIndex: 35,
    text: 'Budowa energii, wody, roślin albo tlenu przesuwa właściwy tor. Gdy wszystkie są powyżej poziomu kolonii, wykonaj aktualizację.',
  },
  {
    id: 'round-end',
    title: 'Koniec rundy',
    chapterIndex: 37,
    text: 'Po wszystkich turach przesuń lub przemieść wahadłowiec. Każdy decyduje o podróży i wybiera nowe pole kolejności.',
  },
  {
    id: 'game-end',
    title: 'Koniec gry',
    chapterIndex: 38,
    text: 'Po dojściu znacznika pozostałych misji do końca dokończ rundę, a potem rozegraj jeszcze jedną rundę kolonizacji bez Fazy Wahadłowca.',
  },
  {
    id: 'scoring',
    title: 'Punktacja',
    chapterIndex: 39,
    text: 'Postęp 1/2/4/7/11; statki ×3; kwatery rzędy 0/1/3/6/10/15; technologie kolumny 1/4/9/16/25; plany ±3/±5; naukowcy ×3 za zgodny budynek zaawansowany; kontrakty wg kart.',
  },
] as const;
